import { useEffect } from 'react';
import { PortingTypes } from '@frontend/api-porting';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ConfirmationModal, TextField, useFormField, ModalControlModalProps, useAlert } from '@frontend/design-system';
import { usePhoneNumbersTableMutations } from './use-phone-numbers-table-mutations';

export const NamePhoneNumberModal = ({
  numberInfo,
  namePhoneNumberProps,
}: {
  numberInfo: PortingTypes.NumberInfo | null;
  namePhoneNumberProps: ModalControlModalProps;
}) => {
  const { t } = useTranslation('porting');
  const alerts = useAlert();
  const phoneHoursNameFieldProps = useFormField({ required: false, type: 'text', value: numberInfo?.displayName });
  const { selectedLocationIds } = useAppScopeStore();
  const { updateName } = usePhoneNumbersTableMutations(selectedLocationIds);

  useEffect(() => {
    if (namePhoneNumberProps.show) {
      phoneHoursNameFieldProps.onChange?.({
        target: { value: numberInfo?.displayName ?? '' },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [namePhoneNumberProps.show]);

  const namePhoneNumber = () =>
    updateName.mutate(
      { phoneNumberId: numberInfo?.id ?? '', displayName: phoneHoursNameFieldProps.value ?? '' },
      {
        onSuccess: () => {
          alerts.success(t('Phone number updated successfully.'));
        },
        onError: () => {
          alerts.error(t('Failed to update phone number.'));
        },
      }
    );

  return (
    <ConfirmationModal
      {...namePhoneNumberProps}
      title={t('Name Phone Number')}
      subLabel={formatPhoneNumber(numberInfo?.phoneNumber?.nationalNumber?.toString() ?? '')}
      message={
        <TextField
          {...phoneHoursNameFieldProps}
          name='phoneNumberName'
          label={t('Phone Number Name')}
          css={{ width: theme.spacing(40) }}
        />
      }
      confirmLabel={t('Save')}
      cancelLabel={t('Cancel')}
      onConfirm={namePhoneNumber}
      maxWidth={600}
    />
  );
};
